import React, { useEffect, useState } from 'react';
import { Download, Inbox, Sparkles, HelpCircle, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../lib/api';
import { toast } from 'sonner';

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/leads').then(({ data }) => setLeads(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const exportCsv = async () => {
    try {
      const res = await API.get('/leads/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'biocloser_leads.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm font-semibold text-white">Captured leads ({leads.length})</h3>
          <p className="text-xs text-muted-foreground">Emails collected by your AI Concierge & Quiz funnels.</p>
        </div>
        <Button onClick={exportCsv} size="sm" disabled={!leads.length} className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime disabled:opacity-50">
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="glass flex flex-col items-center rounded-2xl p-10 text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.06]"><Inbox className="h-6 w-6 text-muted-foreground" /></span>
          <p className="font-display text-sm font-semibold text-white">No leads yet</p>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">When visitors drop their email in the AI chat or quiz, they'll show up here.</p>
        </div>
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <div className="grid grid-cols-12 gap-2 border-b border-white/10 px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <span className="col-span-6">Email</span>
            <span className="col-span-3">Source</span>
            <span className="col-span-3 text-right">Captured</span>
          </div>
          <div className="divide-y divide-white/[0.05]">
            {leads.map((l) => (
              <div key={l.lead_id} className="grid grid-cols-12 items-center gap-2 px-4 py-3 text-sm hover:bg-white/[0.03]">
                <span className="col-span-6 flex items-center gap-2 truncate text-white"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> {l.email}</span>
                <span className="col-span-3">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/12 px-2 py-0.5 text-xs font-medium text-primary">
                    {l.source === 'quiz' ? <HelpCircle className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />} {l.source}
                  </span>
                </span>
                <span className="col-span-3 truncate text-right text-xs text-muted-foreground">{fmt(l.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
